import { useAutoAnimate } from '@formkit/auto-animate/react'
import { GridCellParams, GridValidRowModel } from '@mui/x-data-grid-pro'
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import Trns, { useTranslate } from 'src/localization/Trns'
import {
  accountSettingsUrl,
  createBudgetUrl,
  createVirtualCardUrl,
} from 'src/navigation/urls'
import { css } from 'styled-components'

import styles from './ActionsCell.style'

interface ActionsCellProps<Row extends GridValidRowModel> {
  id: string
  disabled?: boolean
  actionsComponent?: React.ReactElement
  params: GridCellParams<Row>
  showVirtualCardFeatures?: boolean
}

// TODO: move this to the specific component that uses it
export const MoreRowComponent = (props: {
  actionsComponent: React.ReactElement
  id: string
  showVirtualCardFeatures?: boolean
}): React.ReactElement => {
  const { actionsComponent, id, showVirtualCardFeatures } = props
  const DROPDOWN_WIDTH = 200

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [dropdownRef] = useAutoAnimate({ duration: 100, easing: 'ease-in' })
  const buttonRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()

  const getDropdownPosition = (): { top: number; left: number } => {
    if (!buttonRef.current) return { top: 0, left: 0 }
    const rect = buttonRef.current.getBoundingClientRect()
    return {
      top: rect.bottom + window.scrollY,
      left: rect.right - DROPDOWN_WIDTH + window.scrollX,
    }
  }
  const dropdownPosition = getDropdownPosition()

  return (
    <div
      ref={buttonRef}
      css={css`
        position: relative;
      `}
      onMouseEnter={(): void => {
        setIsHovered(true)
        setDropdownOpen(true)
      }}
      onMouseLeave={(): void => {
        setIsHovered(false)
        setDropdownOpen(false)
      }}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
        `}
        onClick={(): void => {
          if (!isHovered) {
            setDropdownOpen(!dropdownOpen)
          }
        }}
        onKeyDown={(e): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            setDropdownOpen(!dropdownOpen)
          }
        }}
        role="button"
        aria-label={translate('More Actions')}
        tabIndex={0}
      >
        {actionsComponent}
      </div>
      {/* using createPortal to render the dropdown outside of the DataGridCellWrapper */}
      {dropdownOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            css={css`
              position: absolute;
              border-radius: 10px;
              display: flex;
              flex-direction: column;
              border: 1px solid ${styleConstants.colors.gray_medium};
              box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);
              width: 200px;
              background-color: ${styleConstants.colors.white};
              padding: 8px 0;
              z-index: 10;
            `}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            {showVirtualCardFeatures && (
              <HoverableLink
                css={css`
                  padding: 8px 16px;
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                  &:hover {
                    text-decoration: underline;
                  }
                `}
                to={createBudgetUrl()}
                onClick={(): void => {
                  setDropdownOpen(false)
                  analytics?.track(
                    'my wallet physical cards quick actions - create a budget - click-in',
                  )
                }}
                ariaLabel="Create a Budget"
              >
                <Trns source={'Create a Budget'} />
              </HoverableLink>
            )}
            {showVirtualCardFeatures && (
              <HoverableLink
                css={css`
                  padding: 8px 16px;
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                  &:hover {
                    text-decoration: underline;
                  }
                `}
                to={createVirtualCardUrl()}
                onClick={(): void => {
                  setDropdownOpen(false)
                  analytics?.track(
                    'my wallet physical cards quick actions - create virtual card - click-in',
                  )
                }}
                ariaLabel="Create a Virtual Card"
              >
                <Trns source={'Create a Virtual Card'} />
              </HoverableLink>
            )}
            <HoverableLink
              css={css`
                padding: 8px 16px;
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.medium,
                )};
                &:hover {
                  text-decoration: underline;
                }
              `}
              to={accountSettingsUrl(id)}
              onClick={(): void => {
                setDropdownOpen(false)
                analytics?.track(
                  'my wallet physical cards quick actions - account settings - click-in',
                )
              }}
              ariaLabel="Account Settings"
            >
              <Trns source={'Account Settings'} />
            </HoverableLink>
          </div>,
          document.body,
        )}
    </div>
  )
}

const ActionsCell = <Row extends GridValidRowModel>(
  props: ActionsCellProps<Row>,
): React.ReactElement => {
  const { id, disabled, actionsComponent, params, showVirtualCardFeatures } =
    props
  return (
    <DataGridCellWrapper disabled={disabled} css={styles} params={params}>
      {actionsComponent && (
        <MoreRowComponent
          actionsComponent={actionsComponent}
          id={id}
          showVirtualCardFeatures={showVirtualCardFeatures}
        />
      )}
      <SvgChevronRight />
    </DataGridCellWrapper>
  )
}

export default ActionsCell
